import React from 'react';
import { Link } from 'react-router-dom';
import { Header, Footer } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  GraduationCap, Clock, Users, Award, CheckCircle, BookOpen, 
  Brain, Heart, MessageCircle, ExternalLink, BadgeCheck, Globe,
  Calendar, MapPin, Laptop, Video
} from 'lucide-react';

const WHATSAPP_NUMBER = '+34635952916';
const WHATSAPP_MESSAGE = '¡Hola Academia Lidera! Vengo desde Sin Adicciones Chile y quiero información sobre el Máster en Adicciones con el descuento especial.';
const WHATSAPP_LINK = `https://wa.me/${WHATSAPP_NUMBER.replace(/\s/g, '')}?text=${encodeURIComponent(WHATSAPP_MESSAGE)}`;

export function FormacionPage() {
  const modules = [
    { title: 'Fundamentos del programa terapéutico', desc: 'Fases de tratamiento, farmacoterapia, autonomía del paciente' },
    { title: 'Terapia cognitiva de drogodependencias', desc: 'TCC, ACT, Mindfulness, modelo transteórico del cambio' },
    { title: 'Familia y trabajo en equipo', desc: 'Enfoque sistémico, dinámicas familiares, intervención' },
    { title: 'Recovery Coaching en adicciones', desc: 'Supervisión clínica, burnout, relación terapéutica' },
    { title: 'Psicología aplicada a las adicciones', desc: 'Negación, mecanismos de defensa, psicoeducación' },
    { title: 'Intervención familiar', desc: 'Impacto en la familia, comunicación no violenta' },
    { title: 'Nuevos modelos terapéuticos', desc: 'Técnica 5 pasos, niño interior, integración emocional' },
    { title: 'Perspectiva de género', desc: 'Género y consumo, vulnerabilidades específicas' },
    { title: 'Inteligencia emocional', desc: 'Neurobiología, circuitos de recompensa, gestión emocional' },
  ];

  const benefits = [
    { icon: BadgeCheck, title: 'Titulación Avalada', desc: 'Diploma respaldado por ANTA y AICM' },
    { icon: Clock, title: '400 Horas', desc: 'Formación completa y flexible' },
    { icon: Laptop, title: 'Modalidad Blended', desc: 'Online + talleres presenciales' },
    { icon: Users, title: 'Profesores en Activo', desc: 'Aprende de expertos que trabajan en el campo' },
  ];

  // Schema for Course
  const courseSchema = {
    "@context": "https://schema.org",
    "@type": "Course",
    "name": "Máster en Adicciones e Intervención Psicosocial",
    "description": "Especialízate en el tratamiento integral de adicciones con técnicas de intervención actualizadas. 400 horas, modalidad blended learning.",
    "provider": {
      "@type": "Organization",
      "name": "Academia Lidera",
      "url": "https://www.academialidera.es"
    },
    "offers": {
      "@type": "Offer",
      "price": "1990",
      "priceCurrency": "EUR",
      "availability": "https://schema.org/InStock"
    }
  };

  return (
    <div className="min-h-screen bg-slate-950">
      
        <title>Formación en Adicciones | Máster con Descuento Especial | Sin Adicciones Chile</title>
        <meta name="description" content="Obtén un descuento exclusivo en el Máster en Adicciones de Academia Lidera. 400 horas de formación, titulación avalada por ANTA y AICM. Alianza oficial con Sin Adicciones Chile." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://sinadicciones.org/formacion" />
        <meta property="og:title" content="Máster en Adicciones - Descuento Exclusivo Sin Adicciones" />
        <meta property="og:description" content="Fórmate como profesional en adicciones con descuento especial por ser parte de Sin Adicciones Chile. 400h, titulación ANTA + AICM." />
        <meta property="og:image" content="https://sinadicciones.org/og-image.png" />
        <meta property="og:image:width" content="1280" />
        <meta property="og:image:height" content="896" />
        <meta property="og:site_name" content="SinAdicciones.org" />
        <meta property="og:locale" content="es_CL" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Máster en Adicciones - Descuento Exclusivo" />
        <meta name="twitter:description" content="400 horas, titulación ANTA + AICM." />
        <meta name="twitter:image" content="https://sinadicciones.org/og-image.png" />
        <link rel="canonical" href="https://sinadicciones.org/formacion" />
        <script type="application/ld+json">{JSON.stringify(courseSchema)}</script>
      
      
      <Header />
      
      <main className="pt-24 pb-16">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-b from-slate-900 to-slate-950 border-b border-slate-800 py-20 overflow-hidden">
          {/* Background decoration */}
          <div className="absolute inset-0 opacity-30">
            <div className="absolute top-20 left-10 w-72 h-72 bg-emerald-500/20 rounded-full blur-3xl"></div>
            <div className="absolute bottom-10 right-10 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl"></div>
          </div>
          
          <div className="max-w-7xl mx-auto px-4 md:px-8 relative z-10">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                {/* Badge */}
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500/10 border border-emerald-500/30 rounded-full mb-6">
                  <BadgeCheck className="w-4 h-4 text-emerald-400" />
                  <span className="text-sm text-emerald-400 font-medium">Alianza Oficial</span>
                </div>
                
                <h1 className="text-4xl md:text-5xl font-bold text-slate-50 mb-6 leading-tight">
                  Máster en Adicciones e Intervención Psicosocial
                </h1>
                
                <p className="text-xl text-slate-300 mb-6">
                  Especialízate con las <strong className="text-emerald-400">técnicas de intervención más efectivas</strong> en el tratamiento integral de adicciones.
                </p>
                
                {/* Discount Badge */}
                <div className="bg-gradient-to-r from-emerald-500/20 to-blue-500/20 border border-emerald-500/40 rounded-xl p-6 mb-8">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="px-3 py-1 bg-emerald-500 text-white text-sm font-bold rounded-full">
                      DESCUENTO EXCLUSIVO
                    </span>
                  </div>
                  <p className="text-slate-300">
                    Como parte de la comunidad <strong className="text-slate-50">Sin Adicciones Chile</strong>, 
                    accede a un <strong className="text-emerald-400">descuento especial</strong> en tu matrícula.
                    Menciona que vienes desde nuestra plataforma.
                  </p>
                </div>
                
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button 
                    asChild
                    size="lg"
                    className="bg-green-600 hover:bg-green-700 text-white rounded-xl px-8 py-6 text-lg font-semibold shadow-lg hover:shadow-xl transition-all"
                  >
                    <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer">
                      <MessageCircle className="w-5 h-5 mr-2" />
                      Solicitar Descuento
                    </a>
                  </Button>
                  <Button 
                    asChild
                    variant="outline"
                    size="lg"
                    className="border-slate-600 text-slate-300 hover:bg-slate-800 rounded-xl px-8 py-6 text-lg"
                  >
                    <a href="https://www.academialidera.es" target="_blank" rel="noopener noreferrer">
                      <ExternalLink className="w-5 h-5 mr-2" />
                      Ver Web Academia
                    </a>
                  </Button>
                </div>
              </div>
              
              {/* Info Card */}
              <div className="lg:pl-8">
                <Card className="bg-slate-900/80 backdrop-blur border-slate-700 shadow-2xl">
                  <CardContent className="p-8">
                    <div className="flex items-center gap-3 mb-6">
                      <img 
                        src="https://www.academialidera.es/wp-content/uploads/2025/04/Logo-Academia-Lidera-Blanco.svg" 
                        alt="Academia Lidera"
                        className="h-10 invert"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                      <span className="text-slate-400">×</span>
                      <span className="font-semibold text-emerald-400">Sin Adicciones</span>
                    </div>
                    
                    <div className="space-y-4 mb-6">
                      <div className="flex items-center gap-3">
                        <Clock className="w-5 h-5 text-emerald-400" />
                        <span className="text-slate-300"><strong className="text-slate-50">400 horas</strong> de formación</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <Calendar className="w-5 h-5 text-emerald-400" />
                        <span className="text-slate-300"><strong className="text-slate-50">9 meses</strong> aproximadamente</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <Laptop className="w-5 h-5 text-emerald-400" />
                        <span className="text-slate-300"><strong className="text-slate-50">Semipresencial</strong> (blended learning)</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <Award className="w-5 h-5 text-emerald-400" />
                        <span className="text-slate-300">Titulación <strong className="text-slate-50">ANTA + AICM</strong></span>
                      </div>
                      <div className="flex items-center gap-3">
                        <Globe className="w-5 h-5 text-emerald-400" />
                        <span className="text-slate-300">Acceso desde <strong className="text-slate-50">cualquier país</strong></span>
                      </div>
                    </div>
                    
                    <div className="border-t border-slate-700 pt-6">
                      <div className="flex items-baseline gap-2 mb-2">
                        <span className="text-3xl font-bold text-slate-50">1.990€</span>
                        <span className="text-slate-500 line-through">+ descuento especial</span>
                      </div>
                      <p className="text-sm text-slate-400">Consulta tu descuento exclusivo por WhatsApp</p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* Benefits */}
        <section className="py-16 border-b border-slate-800">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {benefits.map((benefit, idx) => {
                const Icon = benefit.icon;
                return (
                  <Card key={idx} className="bg-slate-900 border-slate-800">
                    <CardContent className="p-6 text-center">
                      <div className="w-14 h-14 rounded-xl bg-emerald-500/10 mx-auto mb-4 flex items-center justify-center">
                        <Icon className="w-7 h-7 text-emerald-400" />
                      </div>
                      <h3 className="font-semibold text-slate-50 mb-1">{benefit.title}</h3>
                      <p className="text-sm text-slate-400">{benefit.desc}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>

        {/* Who is it for */}
        <section className="py-16 border-b border-slate-800">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <h2 className="text-3xl font-bold text-slate-50 mb-4 text-center">¿Para quién es este máster?</h2>
            <p className="text-slate-400 text-center max-w-2xl mx-auto mb-12">
              Diseñado para profesionales y personas comprometidas con ayudar a quienes sufren adicciones
            </p>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { icon: Brain, title: 'Psicólogos y Terapeutas', desc: 'Que buscan especializarse en adicciones' },
                { icon: Users, title: 'Trabajadores Sociales', desc: 'Que intervienen con colectivos vulnerables' },
                { icon: GraduationCap, title: 'Docentes y Orientadores', desc: 'Que trabajan con jóvenes en riesgo' },
                { icon: Heart, title: 'Personas en Recuperación', desc: 'Que quieren ayudar desde su experiencia' },
              ].map((item, idx) => {
                const Icon = item.icon;
                return (
                  <div key={idx} className="text-center">
                    <div className="w-16 h-16 rounded-2xl bg-slate-800 mx-auto mb-4 flex items-center justify-center">
                      <Icon className="w-8 h-8 text-emerald-400" />
                    </div>
                    <h3 className="font-semibold text-slate-50 mb-2">{item.title}</h3>
                    <p className="text-sm text-slate-400">{item.desc}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Modules */}
        <section className="py-16 border-b border-slate-800 bg-slate-900/50">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <h2 className="text-3xl font-bold text-slate-50 mb-4 text-center">Temario del Máster</h2>
            <p className="text-slate-400 text-center max-w-2xl mx-auto mb-12">
              9 módulos completos + trabajo final de máster
            </p>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {modules.map((mod, idx) => (
                <Card key={idx} className="bg-slate-900 border-slate-800 hover:border-emerald-500/50 transition-colors">
                  <CardContent className="p-5">
                    <div className="flex items-start gap-3">
                      <span className="flex-shrink-0 w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center text-emerald-400 font-bold text-sm">
                        {idx + 1}
                      </span>
                      <div>
                        <h3 className="font-semibold text-slate-50 mb-1 text-sm">{mod.title}</h3>
                        <p className="text-xs text-slate-400">{mod.desc}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Methodology */}
        <section className="py-16 border-b border-slate-800">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <h2 className="text-3xl font-bold text-slate-50 mb-4 text-center">Metodología Blended Learning</h2>
            <p className="text-slate-400 text-center max-w-2xl mx-auto mb-12">
              Aprende a tu ritmo con la flexibilidad que necesitas
            </p>
            
            <div className="grid md:grid-cols-3 gap-8">
              <Card className="bg-slate-900 border-slate-800">
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-xl bg-blue-500/10 mb-4 flex items-center justify-center">
                    <Laptop className="w-6 h-6 text-blue-400" />
                  </div>
                  <h3 className="font-semibold text-slate-50 mb-2">Clases Online</h3>
                  <p className="text-sm text-slate-400">
                    Acceso 24/7 a la plataforma virtual con contenido teórico, material descargable y ejercicios prácticos.
                  </p>
                </CardContent>
              </Card>
              
              <Card className="bg-slate-900 border-slate-800">
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-xl bg-purple-500/10 mb-4 flex items-center justify-center">
                    <Video className="w-6 h-6 text-purple-400" />
                  </div>
                  <h3 className="font-semibold text-slate-50 mb-2">Sesiones en Directo</h3>
                  <p className="text-sm text-slate-400">
                    Sesiones grupales online para resolver dudas, debatir casos y compartir experiencias con compañeros.
                  </p>
                </CardContent>
              </Card>
              
              <Card className="bg-slate-900 border-slate-800">
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-xl bg-emerald-500/10 mb-4 flex items-center justify-center">
                    <MapPin className="w-6 h-6 text-emerald-400" />
                  </div>
                  <h3 className="font-semibold text-slate-50 mb-2">Talleres Presenciales</h3>
                  <p className="text-sm text-slate-400">
                    Jornadas intensivas en España con dinámicas prácticas, simulaciones y trabajo en equipo real.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* CTA Final */}
        <section className="py-20">
          <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
            <div className="bg-gradient-to-r from-emerald-500/10 to-blue-500/10 border border-emerald-500/30 rounded-2xl p-8 md:p-12">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-50 mb-4">
                ¿Listo para dar el siguiente paso?
              </h2>
              <p className="text-lg text-slate-300 mb-8 max-w-2xl mx-auto">
                Consulta tu <strong className="text-emerald-400">descuento exclusivo</strong> por ser parte de Sin Adicciones Chile. 
                Escríbenos por WhatsApp y te damos toda la información.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  asChild
                  size="lg"
                  className="bg-green-600 hover:bg-green-700 text-white rounded-xl px-10 py-6 text-lg font-semibold shadow-lg hover:shadow-xl transition-all"
                >
                  <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer">
                    <MessageCircle className="w-6 h-6 mr-2" />
                    WhatsApp: +34 635 95 29 16
                  </a>
                </Button>
              </div>
              
              <p className="text-sm text-slate-500 mt-6">
                Plazas limitadas · Inscripciones abiertas todo el año
              </p>
            </div>
          </div>
        </section>

        {/* About Academia Lidera */}
        <section className="py-16 border-t border-slate-800">
          <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
            <h3 className="text-xl font-semibold text-slate-50 mb-4">Sobre Academia Lidera</h3>
            <p className="text-slate-400 mb-6">
              Academia Lidera, en colaboración con la red de centros Esvidas – Grupo Sanitario Reinservida, 
              ofrece formación especializada en adicciones desde la experiencia real. Profesores en activo, 
              metodología práctica y un compromiso genuino con la formación de profesionales que marcan la diferencia.
            </p>
            <a 
              href="https://www.academialidera.es" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-emerald-400 hover:text-emerald-300 font-medium inline-flex items-center gap-2"
            >
              Visitar academialidera.es
              <ExternalLink className="w-4 h-4" />
            </a>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
}

export default FormacionPage;
